import type { CircleLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import type { MapCluster } from "../types";

export type MapClusterTone = "property" | "company";

export const CLUSTER_SOURCE_ID = "search-clusters";
export const CLUSTER_CIRCLE_LAYER_ID = "search-clusters-circle";
export const CLUSTER_COUNT_LAYER_ID = "search-clusters-count";

/** Compact count label for a cluster bubble (Danish "t" = tusinde): 1234 → "1.2t", 56000 → "56t". */
export function formatClusterCount(count: number): string {
    if (count < 1000) return String(count);
    const thousands = count / 1000;
    if (count < 10000) return `${thousands.toFixed(1).replace(/\.0$/, "")}t`;
    if (count < 1_000_000) return `${Math.round(thousands)}t`;
    return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, "")} mio.`;
}

/** Server-aggregated clusters → a Mapbox point FeatureCollection (count + label per point). */
export function clustersToGeoJSON(clusters: readonly MapCluster[]): GeoJSON.FeatureCollection<GeoJSON.Point, { count: number; label: string }> {
    return {
        type: "FeatureCollection",
        features: clusters.map((cluster) => ({
            type: "Feature",
            id: cluster.id,
            geometry: { type: "Point", coordinates: [cluster.lng, cluster.lat] },
            properties: { count: cluster.count, label: formatClusterCount(cluster.count) },
        })),
    };
}

const CLUSTER_COLORS: Record<MapClusterTone, [string, string, string, string, string]> = {
    // Blue-600…950. Brand navy matches the property pin, so clusters use this brighter blue.
    property: ["#155dfc", "#1447e6", "#193cb8", "#1c398e", "#162456"],
    // Company entity badge / pin green.
    company: ["#47CD89", "#17B26A", "#099250", "#087443", "#067647"],
};

/**
 * Bubble size + colour ramp stepped on the bucket count — bigger, darker circles
 * carry more hits. These are server-aggregated buckets, so this is a plain
 * circle layer (not Mapbox's client-side `cluster: true`).
 *
 * Hexes are inlined: Mapbox paint cannot read CSS tokens.
 */
export function clusterCircleLayer(tone: MapClusterTone = "property"): Omit<CircleLayerSpecification, "source"> {
    const [c0, c100, c1k, c10k, c100k] = CLUSTER_COLORS[tone];
    return {
        id: CLUSTER_CIRCLE_LAYER_ID,
        type: "circle",
        paint: {
            "circle-color": ["step", ["get", "count"], c0, 100, c100, 1000, c1k, 10000, c10k, 100000, c100k],
            "circle-radius": ["step", ["get", "count"], 14, 100, 18, 1000, 22, 10000, 28, 100000, 34],
            "circle-opacity": 0.9,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
        },
    };
}

export const clusterCountLayer: Omit<SymbolLayerSpecification, "source"> = {
    id: CLUSTER_COUNT_LAYER_ID,
    type: "symbol",
    layout: {
        "text-field": ["get", "label"],
        "text-font": ["Roboto Mono Bold"],
        "text-size": 12,
        "text-allow-overlap": true,
    },
    paint: { "text-color": "#ffffff" },
};
